import { create } from 'zustand';
import { chemistryApi } from '../api/chemistry';

interface FeatureStore {
  features: Record<string, boolean>;
  loaded: boolean;
  loading: boolean;
  generation: number;
  fetchFeatures: () => Promise<void>;
  resetFeatures: () => void;
  hasFeature: (name: string) => boolean;
  isModuleEnabled: (name: string) => boolean;
}

export const useFeatureStore = create<FeatureStore>((set, get) => ({
  features: {},
  loaded: false,
  loading: false,
  generation: 0,

  fetchFeatures: async () => {
    if (get().loaded || get().loading) return;
    const generation = get().generation;
    set({ loading: true });
    try {
      const features = await chemistryApi.getFeatures();
      if (get().generation !== generation) return;
      set({ features, loaded: true, loading: false });
    } catch {
      if (get().generation !== generation) return;
      // Default to all enabled if fetch fails (graceful degradation)
      set({ features: { ip_intelligence: true, target_intelligence: true }, loaded: true, loading: false });
    }
  },

  resetFeatures: () => set((state) => ({
    features: {},
    loaded: false,
    loading: false,
    generation: state.generation + 1,
  })),

  hasFeature: (name: string) => {
    const { features, loaded } = get();
    // If not loaded yet, default to true (don't block UI)
    if (!loaded) return true;
    return features[name] !== false;
  },

  // Opt-out module gating: absent key = visible, explicit false = hidden.
  // Use for mod_* keys only. Do NOT use for premium features (use hasFeature).
  isModuleEnabled: (name: string) => {
    const { features, loaded } = get();
    // Gated navigation remains hidden until the current tenant's flags load.
    if (!loaded) return false;
    return features[name] !== false;
  },
}));

// Hub category → mod_* key. Categories not listed here are always visible.
export const hubCategoryModKey: Record<string, string> = {
  'Biology & Assays': 'mod_assays',
  'ADMET & Safety': 'mod_admet',
  'Pharmacology': 'mod_pharmacology',
  'Genomics & Omics': 'mod_genomics',
  'Biologics': 'mod_biologics',
  'Computational': 'mod_computational',
  'Clinical': 'mod_clinical',
};

// Hub id → mod_* key, for hubs gated independently of their category.
// Takes precedence over hubCategoryModKey.
export const hubModKey: Record<string, string> = {
  eln: 'mod_eln',
};

// Resolution order: per-hub override, then category, then undefined (visible).
export function resolveHubModKey(hubId: string, category: string): string | undefined {
  return hubModKey[hubId] ?? hubCategoryModKey[category];
}
